import React, { useEffect, useState } from "react";
import { useHttpGet } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { MEMBER_KEY } from "../../../utils/constants";
import { REIButton, REIDataTable, REIDialoge, REIProgressSpinner } from "../../../components/ui";
import PropertyInfo from "../../../components/shared/propertyInfo";
import PropertyInvestmentCalculator from "./PropertyInvestmentCalculator";
import { useLocation, useNavigate } from "react-router-dom";
import { fetchFormattedAddress, shouldEnrichAddress } from "../../../utils/commonUtil";

const AnalyserWatchList: React.FC<{ streetAddress: string }> = ({ streetAddress }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [showPropertyInvestmentCalculator, setShowPropertyInvestmentCalculator] = useState<boolean>(false);
  const [selectedWathclistItem, setSelectedWathclistItem] = useState<any>(null);
  const [formattedWatchlistData, setFormattedWatchlistData] = useState<any[]>([]);
  const { data: watchlistData, error } = useHttpGet(
    `${ApiEndPoints.analyser}/Watchlist/${localStorage.getItem(MEMBER_KEY)}`,
    undefined,
    true
  );
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    let isActive = true;

    const load = async () => {
      if (!watchlistData) return;
      setLoading(false);

      const apiKey = process.env.GOOGLE_MAPS_API_KEY;
      const formatted = await Promise.all(
        watchlistData.map(async (item: any) => {
          const baseAddress = `${item.StreetAddress}, ${item.Suburb}, ${item.StateRegion}`;
          if (!apiKey || !shouldEnrichAddress(baseAddress)) {
            return { ...item, propertyAddress: baseAddress };
          }

          const formattedAddress = await fetchFormattedAddress(baseAddress, apiKey, item.country === "AU" ? 1 : 2);
          return { ...item, propertyAddress: formattedAddress || baseAddress };
        })
      );

      if (!isActive) return;

      setFormattedWatchlistData(formatted);

      if (streetAddress && streetAddress != "" && formatted.length > 0) {
        const filteredData = formatted.filter(
          (item: any) =>
            item.StreetAddress.toLowerCase().includes(streetAddress.toLowerCase()) ||
            item.Suburb.toLowerCase().includes(streetAddress.toLowerCase())
        );
        if (filteredData.length > 0) {
          setSelectedWathclistItem(filteredData[0]);
          setShowPropertyInvestmentCalculator(true);
        }
      }
    };

    load();

    return () => {
      isActive = false;
    };
  }, [streetAddress, watchlistData]);

  if (loading) {
    return (
      <div className="text-center">
        <REIProgressSpinner />
      </div>
    );
  }

  if (!watchlistData || !watchlistData.length) {
    return <div className="text-center">No data selected for analyser.</div>;
  }

  const handleOnAddressClick = (rowData: any) => {
    if (rowData.isAnalysed !== "1") {
      setSelectedWathclistItem(rowData);
      setShowPropertyInvestmentCalculator(true);
    } else {
      navigate("/property-analyser/analysis-report", {
        state: {
          propertyAnalyserId: rowData.PropetyAnalyserId,
          propertyName: `${rowData.propertyAddress || `${rowData.StreetAddress}, ${rowData.Suburb}, ${rowData.StateRegion}`}`,
          countryID: rowData.country === "AU" ? 1 : 2,
        },
      });
    }
  };

  const hidePropertyInvcestmentCalculator = () => {
    setSelectedWathclistItem(null);
    setShowPropertyInvestmentCalculator(false);
    navigate(location.pathname, {
      replace: true,
      state: {
        ...location.state,
        propertyName: "",
      },
    });
  };

  const renderAddress = (rowData: any) => (
    <>
      <div className="flex flex-row w-full text-[#4e9fba]">
        <span
          id={`pawl-lnk-address-${rowData.WatchlistId}`}
          data-testid={`pawl-lnk-address-${rowData.WatchlistId}`}
          className="mr-1 pt-2 cursor-pointer"
          onClick={() => {
            handleOnAddressClick(rowData);
          }}
        >
          {rowData.propertyAddress || `${rowData.StreetAddress}, ${rowData.Suburb}, ${rowData.StateRegion}`}
        </span>
      </div>
      <PropertyInfo bathrooms={rowData.Bathrooms} bedrooms={rowData.Bedrooms} carport={rowData.Carport} />
    </>
  );

  const renderAnalysedStatus = (rowData: any) => (
    <div className="text-center">
      {rowData.isAnalysed === "1" ? (
        <REIButton id={`pawl-btn-analysedyes-${rowData.WatchlistId}`} icon="pi pi-check" text={true} onClick={() => {}} severity="success" />
      ) : (
        <REIButton id={`pawl-btn-analysedno-${rowData.WatchlistId}`} icon="pi pi-minus" text={true} onClick={() => {}} severity="danger" />
      )}
    </div>
  );

  const columns = [
    {
      field: "address",
      header: "Address",
      bodyTemplate: renderAddress,
    },
    {
      field: "isAnalysed",
      header: "Analysed?",
      width: "100px",
      bodyTemplate: renderAnalysedStatus,
    },
  ];

  const tableData = formattedWatchlistData.length > 0 ? formattedWatchlistData : watchlistData || [];

  return (
    <>
      <REIDialoge
        showHeader={true}
        header={`Calculate property's investment potential`}
        visible={showPropertyInvestmentCalculator}
        handleEditClick={hidePropertyInvcestmentCalculator}
        position="top"
        style={{ width: "55vw" }}
        customClass="edit-default-profile"
        contentComponent={
          <PropertyInvestmentCalculator
            closeDialog={hidePropertyInvcestmentCalculator}
            wathclistItem={selectedWathclistItem}
          />
        }
      ></REIDialoge>
      <div className="overflow-y-auto min-h-[164px] altRowShade">
        <REIDataTable data={tableData} columns={columns} actions={undefined} customclass="max-h-[164px]" />
      </div>
    </>
  );
};

export default AnalyserWatchList;
